import React, { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useAdminPermissions from '../../hooks/useAdminPermissions';
import { API_URL } from '../../utils/api';
import AdminElo from './AdminElo';
import AdminPlayers from './AdminPlayers';
import AdminSettings from './AdminSettings';
import AdminDrops from './AdminDrops';
import AdminQueue from './AdminQueue';
import AdminMatches from './AdminMatches';
import AdminOverview from './AdminOverview';
import AdminTournaments from './AdminTournaments';
import AdminMedals from './AdminMedals';
import AdminNews from './AdminNews';
import AdminShop from './AdminShop';
import AdminThemes from './AdminThemes';
import AdminNotices from './AdminNotices';
import AdminMaps from './AdminMaps';
import AdminAccessRoles from './AdminAccessRoles';
import AdminDiagnostics from './AdminDiagnostics';
import AdminTools from './AdminTools';
import AdminGslt from './AdminGslt';
import AdminDatHostBudget from './AdminDatHostBudget';
import AdminHost from './AdminHost';
import AdminEconomy from './AdminEconomy';
import ADMIN_SECTIONS from './adminSections';
import './AdminLayout.css';

/**
 * Admin panel layout with grouped sidebar navigation and permission-gated tabs.
 */
export default function AdminLayout() {
  const { t } = useTranslation();
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const perms = useAdminPermissions();
  const [pendingMapTips, setPendingMapTips] = useState(0);

  // Poll pending map-suggestion count so the "Kartor" tab gets a badge.
  // Cheap endpoint (single COUNT query) — safe to refresh every 30s.
  useEffect(() => {
    if (!isLoggedIn || !perms.isOwner) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/maps/suggestions/pending-count`, {
          headers: getAuthHeaders(),
        });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setPendingMapTips(d.count || 0);
        }
      } catch { /* ignore — badge is best-effort */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isLoggedIn, perms.isOwner, getAuthHeaders]);

  if (perms.loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">{t('live.loading')}</div>
      </div>
    );
  }

  const hasAccess = ADMIN_SECTIONS.some((s) => s.check(perms));

  if (!isLoggedIn || !hasAccess) {
    return (
      <div className="admin-page">
        <div className="admin-no-access">{t('admin.noAccess')}</div>
      </div>
    );
  }

  const visibleSections = ADMIN_SECTIONS.filter((s) => s.check(perms));

  // Find first visible section for default redirect
  const defaultSection = visibleSections.length > 0 ? visibleSections[0].path : '';

  // Render sidebar links grouped by their `group`, with a label per group.
  let lastGroup = null;

  return (
    <div className="admin-page">
      <div className="admin-container">
        <nav className="admin-sidebar">
          <h2 className="admin-sidebar-title">{t('admin.title')}</h2>
          {visibleSections.map((section) => {
            const showGroupLabel = section.group && section.group !== lastGroup;
            lastGroup = section.group;
            return (
              <React.Fragment key={section.path}>
                {showGroupLabel && (
                  <div className="admin-sidebar-group-label">{t(section.groupKey)}</div>
                )}
                <NavLink
                  to={`/webadmin/${section.path}`}
                  end={section.path === ''}
                  className={({ isActive }) =>
                    `admin-sidebar-link ${isActive ? 'active' : ''}`
                  }
                >
                  {t(section.labelKey)}
                  {section.path === 'maps' && pendingMapTips > 0 && (
                    <span className="admin-sidebar-badge" title={t('admin.pendingTips', { count: pendingMapTips })}>
                      {pendingMapTips}
                    </span>
                  )}
                </NavLink>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="admin-content">
          <Routes>
            <Route path="" element={perms.isOwner ? <AdminOverview perms={perms} /> : <Navigate to={defaultSection} replace />} />
            <Route path="players" element={<AdminPlayers perms={perms} />} />
            <Route path="elo" element={<AdminElo perms={perms} />} />
            <Route path="matches" element={<AdminMatches perms={perms} />} />
            <Route path="drops" element={<AdminDrops perms={perms} />} />
            <Route path="queue" element={<AdminQueue perms={perms} />} />
            <Route path="tournaments/*" element={<AdminTournaments perms={perms} />} />
            <Route path="medals" element={<AdminMedals perms={perms} />} />
            <Route path="news" element={<AdminNews perms={perms} />} />
            <Route path="shop" element={<AdminShop perms={perms} />} />
            <Route path="themes" element={<AdminThemes perms={perms} />} />
            <Route path="maps" element={<AdminMaps perms={perms} />} />
            <Route path="settings" element={<AdminSettings perms={perms} />} />
            <Route path="notices" element={<AdminNotices perms={perms} />} />

            {/* Merged containers */}
            <Route path="access-roles" element={<AdminAccessRoles perms={perms} />} />
            <Route path="diagnostics" element={<AdminDiagnostics perms={perms} />} />
            <Route path="verktyg" element={<AdminTools perms={perms} />} />
            <Route path="gslt" element={<AdminGslt perms={perms} />} />
            <Route path="dathost-budget" element={<AdminDatHostBudget />} />
            <Route path="host" element={<AdminHost perms={perms} />} />
            <Route path="economy" element={<AdminEconomy perms={perms} />} />

            {/* Legacy routes → redirect to the right sub-tab so old bookmarks survive */}
            <Route path="access" element={<Navigate to="/webadmin/access-roles?sub=access" replace />} />
            <Route path="invites" element={<Navigate to="/webadmin/access-roles?sub=invites" replace />} />
            <Route path="permissions" element={<Navigate to="/webadmin/access-roles?sub=permissions" replace />} />
            <Route path="web-roles" element={<Navigate to="/webadmin/access-roles?sub=web-roles" replace />} />
            <Route path="users" element={<Navigate to="/webadmin/access-roles?sub=users" replace />} />
            <Route path="logs" element={<Navigate to="/webadmin/diagnostics?sub=logs" replace />} />
            <Route path="mcp" element={<Navigate to="/webadmin/diagnostics?sub=mcp" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
